/** A year, and the month when the member gave one (the wizard's YearMonth). */
export interface YearMonth {
  year: number;
  month?: number | null;
}

/**
 * T.10 §9.6. The starter summary, from profile facts only. Shared by the
 * wizard (live, as the member toggles) and the subgraph's resumeStarter. It never mentions
 * a degree status and never states a number the profile does not hold.
 */

export interface SummaryJob {
  title: string;
  monthsHeld?: number;
  start?: YearMonth;
  end?: YearMonth;
  current?: boolean;
}

export interface SummaryInput {
  /** "U.S. Army". */
  branch?: string;
  veteran?: boolean;
  /** "Sergeant (E-5)"; printed without the pay grade. */
  rank?: string;
  /** Kept military jobs, most recent first. */
  military: SummaryJob[];
  /** Kept civilian jobs, most recent first. */
  civilian: SummaryJob[];
  /** Kept skills in rank order; the first two are named. */
  skills: string[];
  certification?: string;
  today: YearMonth;
}

/** Words that keep their capital in "skilled in ...". */
const PROPER = new Set([
  'English', 'Spanish', 'French', 'German', 'Arabic', 'Korean', 'Chinese', 'Japanese', 'Russian',
  'Microsoft', 'Windows', 'Excel', 'Linux', 'Cisco', 'Python', 'Java', 'Office'
]);

/** "Computers and Electronics" to "computers and electronics", "ServiceNow" kept. */
export function lowerSkill(skill: string): string {
  return skill
    .split(' ')
    .map((w) => (PROPER.has(w) || /[A-Z].*[A-Z]/.test(w) ? w : w.toLowerCase()))
    .join(' ');
}

/** "a" or "an" by sound: an IT, an HR, an MRI; a user, a one-person; an hour. */
export function article(phrase: string): 'a' | 'an' {
  const word = phrase.trim().split(/\s+/)[0] ?? '';
  if (/^[A-Z0-9]{2,}$/.test(word)) {
    return /^[AEFHILMNORSX8]/.test(word) ? 'an' : 'a';
  }
  if (/^(hour|honest|honor|heir)/i.test(word)) {
    return 'an';
  }
  if (/^(uni|use|usu|eu|one|once|ur)/i.test(word)) {
    return 'a';
  }
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

const nowIndex = (today: YearMonth) => today.year * 12 + (today.month ?? 6);
const idx = (ym: YearMonth, fallbackMonth: number) => ym.year * 12 + (ym.month ?? fallbackMonth);

/**
 * Years of service (§9.5): the military tours' date spans joined (a gap
 * between tours is not service) when every military job has its dates;
 * otherwise the sum of months held. Rounded to whole years, so "about".
 */
export function serviceYears(
  jobs: SummaryJob[],
  today: YearMonth
): { years: number; months: number; about: boolean } | null {
  if (!jobs.length) {
    return null;
  }
  const dated = jobs.every((j) => j.start && (j.end || j.current));
  if (dated) {
    // The tours' spans joined: overlaps count once, gaps between tours not at all.
    const spans = jobs
      .map((j) => [idx(j.start as YearMonth, 1), j.current || !j.end ? nowIndex(today) : idx(j.end, 12)])
      .sort((a, b) => a[0] - b[0]);
    let months = 0;
    let [s, e] = spans[0];
    for (const [a, b] of spans.slice(1)) {
      if (a <= e) {
        e = Math.max(e, b);
      } else {
        months += Math.max(0, e - s);
        [s, e] = [a, b];
      }
    }
    months += Math.max(0, e - s);
    // Rounded to whole years, so "about" either way.
    return months ? { years: Math.round(months / 12), months, about: true } : null;
  }
  const months = jobs.reduce((sum, j) => sum + (j.monthsHeld ?? 0), 0);
  return months ? { years: Math.round(months / 12), months, about: true } : null;
}

function yearsPhrase(span: { years: number; months: number; about: boolean }, noun: string): string {
  if (span.months < 12) {
    return `with less than a year of ${noun}`;
  }
  const n = `${span.years} ${span.years === 1 ? 'year' : 'years'}`;
  return `with ${span.about ? 'about ' : ''}${n} of ${noun}`;
}

const sentenceCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The jobs most recent first (S2 review): a current job, then by end, then
 * by start; a job with no dates keeps its place after the dated ones. The
 * summary leads with the first, whatever order the jobs arrive in.
 */
export function mostRecentFirst<T extends SummaryJob>(jobs: readonly T[]): T[] {
  const rank = (j: SummaryJob): number =>
    j.current ? Number.MAX_SAFE_INTEGER : j.end ? idx(j.end, 12) : j.start ? idx(j.start, 1) : -1;
  return jobs
    .map((j, i) => ({ j, i }))
    .sort((a, b) => rank(b.j) - rank(a.j) || a.i - b.i)
    .map((x) => x.j);
}

function lead(input: SummaryInput): string {
  const mil = mostRecentFirst(input.military)[0];
  if (mil) {
    const branch = input.branch ? `${input.branch} ` : '';
    const title = lowerSkill(mil.title);
    const who = input.veteran ? `${branch}veteran and former ${title}` : `${branch}${title}`;
    const span = serviceYears(input.military, input.today);
    if (span) {
      return `${who} ${yearsPhrase(span, 'service')}`;
    }
    const rank = input.rank?.replace(/\s*\(.*\)$/, '').trim();
    return rank ? `${who} (${rank})` : who;
  }
  const civ = mostRecentFirst(input.civilian)[0];
  if (!civ) {
    return '';
  }
  const span = serviceYears(input.civilian, input.today);
  const title = sentenceCase(lowerSkill(civ.title));
  return span ? `${title} ${yearsPhrase(span, 'experience')}` : title;
}

/** The summary, or empty when there is no job to lead with. */
export function starterSummary(input: SummaryInput): string {
  const first = lead(input);
  if (!first) {
    return '';
  }
  const top = input.skills.slice(0, 2).map(lowerSkill);
  let s = sentenceCase(first) + (top.length ? `, skilled in ${top.join(' and ')}` : '') + '.';
  // S18 (D126): no aim sentence. The summary never names the goal title, a
  // posting's title or an employer: "Looking for a ... role" reads as a template.
  if (input.certification) {
    s += ` Holds ${input.certification}.`;
  }
  return s;
}
